import {
  mediaQueryLarge,
  requestIdleCallback,
  startViewTransition,
} from "@theme/utilities";
import PaginatedList from "@theme/paginated-list";

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener("change", this.#handleMediaQueryChange);
    this.setAttribute("initialized", "");

    this.restoreProductGridScroll();
  }

  /**
   * Restore scroll position and product card if available and page matches
   */
  restoreProductGridScroll() {
    try {
      const stateRaw = sessionStorage.getItem("product-grid-state");
      if (stateRaw) {
        const state = JSON.parse(stateRaw);
        const url = new URL(window.location.href);
        const currentPage = url.searchParams.get("page") || "1";
        if (state.page === currentPage) {
          sessionStorage.removeItem("product-grid-state");
          // Try to scroll to the product card with data-product-id, retry for up to 500ms
          let attempts = 0;
          const maxAttempts = 10;
          const tryScroll = () => {
            if (state.productId) {
              const el = document.querySelector(
                `[data-product-id="${state.productId}"]`,
              );
              if (el) {
                el.scrollIntoView({ behavior: "instant", block: "center" });
                return;
              }
            }
            if (attempts < maxAttempts) {
              attempts++;
              setTimeout(tryScroll, 50);
            } else {
              window.scrollTo({
                top: parseInt(state.scroll, 10) || 0,
                behavior: "instant",
              });
            }
          };
          requestAnimationFrame(tryScroll);
        }
      }
    } catch (e) {}
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener("change", this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ["product-grid"]);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? "desktop" : "mobile";
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute("product-grid-view", value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout("default");
  };
}

if (!customElements.get("results-list")) {
  customElements.define("results-list", ResultsList);
}
